import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { UserProvider } from './contexts/UserContext';
import { BatchCartProvider } from './contexts/BatchCartContext';
import { NavigationHistoryProvider } from './contexts/NavigationHistoryContext';
import ProtectedLayout from './layouts/ProtectedLayout';
import TosGate from './components/Auth/TosGate';
import DisplayNameGate from './components/Auth/DisplayNameGate';
import BugReporterButton from './components/Common/BugReporter/BugReporterButton';
import BatchCartDrawer from './components/ReviewDashboard/BatchCartDrawer';
import ScrollToTop from './components/Common/ScrollToTop';
import ErrorBoundary from './components/Common/ErrorBoundary';
import { publicRoutes, protectedRoutes } from './config/routes';
import { Link } from 'react-router-dom';
import './App.css';

// 404 page for undefined routes
const NotFoundPage = () => (
  <div className="max-w-md mx-auto mt-20 text-center">
    <div className="card p-8">
      <h1 className="text-4xl font-bold text-gray-300 mb-2">404</h1>
      <h2 className="text-xl font-semibold text-foreground mb-2">Page Not Found</h2>
      <p className="text-muted-foreground mb-6">
        The page you're looking for doesn't exist or has been moved.
      </p>
      <Link to="/dashboard" className="btn-primary inline-block">
        Go to Dashboard
      </Link>
    </div>
  </div>
);

// Loading fallback for lazy-loaded components
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <svg className="w-8 h-8 animate-spin text-primary" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
      <span className="text-muted-foreground text-sm">Loading...</span>
    </div>
  </div>
);

function App() {
  return (
    <Router>
      <ScrollToTop />
      <AuthProvider>
        <UserProvider>
          <BatchCartProvider>
          <NavigationHistoryProvider>
          <TosGate>
          <DisplayNameGate>
            <BugReporterButton />
            <BatchCartDrawer />
            <div className="App">
              <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  {/* Public routes (no authentication required) */}
                  {publicRoutes.map((route) => (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={route.element}
                    />
                  ))}

                  {/* Protected routes (require authentication) */}
                  {protectedRoutes.map((route) => (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={
                        <ProtectedLayout>
                          {route.element}
                        </ProtectedLayout>
                      }
                    />
                  ))}

                  {/* Catch-all 404 route */}
                  <Route
                    path="*"
                    element={
                      <ProtectedLayout>
                        <NotFoundPage />
                      </ProtectedLayout>
                    }
                  />
                </Routes>
              </Suspense>
              </ErrorBoundary>
            </div>
          </DisplayNameGate>
          </TosGate>
          </NavigationHistoryProvider>
          </BatchCartProvider>
        </UserProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
